import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowRight, Send } from 'lucide-react';
import {
  DISPATCH_CYCLE_NOTE_LABELS,
  DISPATCH_DEFAULT_DAILY_CAP,
  DISPATCH_DEFAULT_WEEKDAYS,
  DISPATCH_DEFAULT_WINDOW,
  DISPATCH_PARAM_SOURCES,
  TICKET_DISPATCH_STATUS_LABELS,
  TICKET_DISPATCH_STATUSES,
  BLIP_TEMPLATE_BODY_MAX,
  dispatchParamSourceLabel,
  dispatchReasonLabel,
  renderTemplateParams,
  templateParamCount,
  whatsappChannelLabel,
  type DeskDetail,
  type DispatchParam,
  type DispatchParamSource,
  type TicketDispatchRule,
} from '@erp/shared';
import { useAuthUser } from '@/auth/use-auth';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input, Label, Select } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge, Skeleton } from '@/components/ui/misc';
import { formatDateTime } from '@/lib/utils';
import { errorMessage } from '@/features/desks/desks-queries';
import { useTerms } from '@/shared/tenant-profile';
import { whatsappChannelsOptions } from '@/features/whatsapp/whatsapp-queries';
import {
  templateFailureMessage,
  templatesOfNumber,
  whatsappTemplatesOptions,
} from '@/features/chats/chats-queries';
import {
  deskDispatchRuleOptions,
  deskDispatchSummaryOptions,
  useSaveDispatchRule,
} from '@/features/tickets/dispatch-queries';

const WEEKDAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

/**
 * Disparo por ticket (spec 014): a regra que substitui o worker do pooltrack
 * na mesa — modelo de WhatsApp, estágios, cota, dias, janela e clientes
 * bloqueados —, e o painel do dia. Só admin edita (a API responde 403 aos
 * demais); todo mundo vê o painel.
 *
 * Ligar numa mesa espelhada do TiFlux é recusado (409 desk_mirrored): o
 * espelho desfaria as trocas de estágio. Salvar DESLIGADA é permitido, para
 * deixar tudo pronto antes da virada da mesa.
 *
 * Spec 025, fase 3: a exceção é o número da BLIP. Numa mesa espelhada a fila
 * só alcança o ticket fora do espelho de cliente operado no ERP, e o modelo sai
 * pelo número oficial da PoolTrack — só números da Blip aparecem para escolha.
 * Os modelos desse número vivem na conta da Blip e chegam pelo broker: a lista
 * do número é a primeira opção, com o texto aprovado. Informar à mão (nome,
 * idioma e texto) fica de reserva: modelo fora da lista, ou a lista da Blip
 * sem carregar. E a etapa de resposta é a de envio: é nela que o nosso bot
 * acha o ticket.
 */
export function DeskDispatchSection({ desk }: { desk: DeskDetail }) {
  const user = useAuthUser();
  const isAdmin = user?.role === 'admin';
  const ruleQ = useQuery(deskDispatchRuleOptions(desk.id));

  return (
    <section className="rounded-lg border bg-card p-4 shadow-xs">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <Send className="size-4 text-muted-foreground" />
        <h2 className="text-base font-semibold">Disparo</h2>
        {ruleQ.data ? (
          <Badge variant={ruleQ.data.enabled ? 'success' : 'default'}>
            {ruleQ.data.enabled ? 'Ligado' : 'Desligado'}
          </Badge>
        ) : null}
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Envia o modelo de WhatsApp para cada ticket que chega no estágio de entrada, em ordem de
        chegada, dentro dos dias, horário e cota do dia. A resposta do cliente liga a conversa ao
        ticket e o move para o estágio de resposta.
      </p>

      {ruleQ.isPending ? (
        <Skeleton className="h-24 w-full" />
      ) : ruleQ.isError ? (
        <p className="text-sm text-destructive">{errorMessage(ruleQ.error)}</p>
      ) : (
        <div className="grid items-start gap-4 xl:grid-cols-3">
          <div className="xl:col-span-2">
            {isAdmin ? (
              <DispatchRuleForm desk={desk} rule={ruleQ.data} />
            ) : ruleQ.data ? (
              <p className="text-sm text-muted-foreground">
                Modelo <code>{ruleQ.data.templateName}</code>, até {ruleQ.data.dailyCap} por dia,
                das {ruleQ.data.windowStart} às {ruleQ.data.windowEnd} (
                {ruleQ.data.weekdays.map((d) => WEEKDAY_LABELS[d]).join(', ')}).
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">Esta mesa não tem regra de disparo.</p>
            )}
          </div>
          {ruleQ.data ? <DispatchPanel deskId={desk.id} rule={ruleQ.data} /> : null}
        </div>
      )}
    </section>
  );
}

/**
 * O painel do dia da regra (GET /desks/:id/dispatch-summary): na config da
 * mesa e no topo da lista "Disparos". `listLink` leva da config para a lista
 * já filtrada pela mesa — na própria lista ele não aparece.
 */
export function DispatchPanel({
  deskId,
  rule,
  listLink = true,
}: {
  deskId: number;
  rule: TicketDispatchRule;
  listLink?: boolean;
}) {
  const q = useQuery(deskDispatchSummaryOptions(deskId));
  const terms = useTerms();
  if (q.isPending) return <Skeleton className="h-32 w-full" />;
  if (q.isError) return <p className="text-sm text-destructive">{errorMessage(q.error)}</p>;
  const s = q.data;
  return (
    <div className="space-y-3 rounded-md border bg-muted/30 p-3 text-sm">
      <div className="grid grid-cols-3 gap-2 text-center">
        <Metric label="Enviados hoje" value={`${s.sentToday} / ${s.dailyCap}`} />
        <Metric label="Na fila" value={String(s.queued)} />
        <Metric label="Responderam hoje" value={String(s.repliedToday)} />
      </div>
      {s.requeued.length > 0 || s.waitingTiflux > 0 ? (
        <div>
          <div className="mb-1 text-xs font-medium text-muted-foreground">Na fila, esperando</div>
          <ul className="space-y-0.5 text-xs">
            {s.requeued.map((r) => (
              <li key={r.reason} className="flex justify-between gap-2">
                <span>{dispatchReasonLabel(r.reason, terms) ?? r.reason}</span>
                <span className="tabular-nums">{r.count}</span>
              </li>
            ))}
            {s.waitingTiflux > 0 ? (
              <li className="flex justify-between gap-2">
                <span>{dispatchReasonLabel('waiting:tiflux_stage', terms)}</span>
                <span className="tabular-nums">{s.waitingTiflux}</span>
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}
      {s.failuresToday.length > 0 ? (
        <div>
          <div className="mb-1 text-xs font-medium text-muted-foreground">
            Falhas e ignorados hoje
          </div>
          <ul className="space-y-0.5 text-xs">
            {s.failuresToday.map((f) => (
              <li key={`${f.status}-${f.reason}`} className="flex justify-between gap-2">
                <span
                  className={f.status === 'failed' ? 'text-destructive' : 'text-muted-foreground'}
                >
                  {dispatchReasonLabel(f.reason, terms) ?? '—'}
                </span>
                <span className="tabular-nums">{f.count}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <details className="text-xs">
        <summary className="cursor-pointer text-muted-foreground">Todos os status</summary>
        <ul className="mt-1 space-y-0.5">
          {TICKET_DISPATCH_STATUSES.map((st) => (
            <li key={st} className="flex justify-between">
              <span>{TICKET_DISPATCH_STATUS_LABELS[st]}</span>
              <span className="tabular-nums">{s.byStatus[st] ?? 0}</span>
            </li>
          ))}
        </ul>
      </details>
      {rule.lastCycleAt ? (
        <p className="text-xs text-muted-foreground">
          Último ciclo {formatDateTime(rule.lastCycleAt)}:{' '}
          {DISPATCH_CYCLE_NOTE_LABELS[rule.lastCycleNote ?? ''] ?? rule.lastCycleNote}
        </p>
      ) : null}
      {listLink ? (
        <Link
          to="/tickets/dispatches"
          search={{ desk: deskId }}
          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
        >
          Ver disparos
          <ArrowRight className="size-3" />
        </Link>
      ) : null}
    </div>
  );
}

/** O painel de uma mesa fora da config dela (a lista "Disparos"): busca a regra e o resumo. */
export function DeskDispatchSummary({ deskId, deskName }: { deskId: number; deskName: string }) {
  const ruleQ = useQuery(deskDispatchRuleOptions(deskId));
  return (
    <div className="space-y-1.5">
      <h2 className="text-sm font-semibold">{deskName}</h2>
      {ruleQ.isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : ruleQ.isError ? (
        <p className="text-sm text-destructive">{errorMessage(ruleQ.error)}</p>
      ) : ruleQ.data ? (
        <DispatchPanel deskId={deskId} rule={ruleQ.data} listLink={false} />
      ) : null}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

interface FormState {
  enabled: boolean;
  /** spec 031 (D5): exigir a ficha ao abrir ticket à mão nesta mesa */
  requireSheetOnManual: boolean;
  channelId: number | '';
  templateKey: string;
  /** canal da Blip (spec 025): o modelo informado à mão — nome, idioma, texto */
  blipTemplateName: string;
  blipTemplateLanguage: string;
  blipTemplateBody: string;
  params: DispatchParam[];
  entryStageId: number | '';
  sentStageId: number | '';
  invalidStageId: number | '';
  repliedStageId: number | '';
  dailyCap: number;
  weekdays: number[];
  windowStart: string;
  windowEnd: string;
  blockedClientIds: string;
}

const templateKeyOf = (name: string, language: string) => `${name}|${language}`;
/** canal da Blip: "Outro — informar à mão" no select da lista */
const MANUAL_KEY = '__manual__';

function initialState(rule: TicketDispatchRule | null): FormState {
  return {
    enabled: rule?.enabled ?? false,
    requireSheetOnManual: rule?.requireSheetOnManual ?? true,
    channelId: rule?.channelId ?? '',
    templateKey: rule ? templateKeyOf(rule.templateName, rule.templateLanguage) : '',
    blipTemplateName: rule?.templateName ?? 'integraai_agendar_instalacao',
    blipTemplateLanguage: rule?.templateLanguage ?? 'pt_BR',
    blipTemplateBody: rule?.templateBody ?? '',
    params: rule?.params ?? [],
    entryStageId: rule?.entryStageId ?? '',
    sentStageId: rule?.sentStageId ?? '',
    invalidStageId: rule?.invalidStageId ?? '',
    repliedStageId: rule?.repliedStageId ?? '',
    dailyCap: rule?.dailyCap ?? DISPATCH_DEFAULT_DAILY_CAP,
    weekdays: rule?.weekdays ?? [...DISPATCH_DEFAULT_WEEKDAYS],
    windowStart: rule?.windowStart ?? DISPATCH_DEFAULT_WINDOW.start,
    windowEnd: rule?.windowEnd ?? DISPATCH_DEFAULT_WINDOW.end,
    blockedClientIds: (rule?.blockedClientIds ?? []).join(', '),
  };
}

function DispatchRuleForm({ desk, rule }: { desk: DeskDetail; rule: TicketDispatchRule | null }) {
  const terms = useTerms();
  const channelsQ = useQuery(whatsappChannelsOptions());
  const templatesQ = useQuery(whatsappTemplatesOptions());
  const save = useSaveDispatchRule(desk.id);
  const [form, setForm] = useState<FormState>(() => initialState(rule));
  useEffect(() => setForm(initialState(rule)), [rule]);

  const stages = useMemo(
    () => [...desk.stages].sort((a, b) => a.position - b.position),
    [desk.stages]
  );
  const channels = channelsQ.data ?? [];
  const channel = channels.find((c) => c.id === form.channelId);
  // spec 025, fase 3: mesa espelhada só dispara pelo número da Blip
  const mirroredDesk = desk.origin === 'tiflux';
  const isBlip = channel?.provider === 'blip';
  // os modelos do NÚMERO do canal (spec 025: o da Blip tem os da conta da Blip)
  const numberList = templatesOfNumber(templatesQ.data, channel?.phoneNumberId ?? null);
  const listTemplate = numberList.templates.find(
    (t) => templateKeyOf(t.name, t.language) === form.templateKey
  );
  // canal da Blip: a lista do número quando ela tem modelos; à mão quando a
  // pessoa escolhe "Outro", quando o modelo salvo não está nela, ou quando a
  // lista não veio (vazia ou com falha)
  const blipListUsable = isBlip && numberList.templates.length > 0;
  const blipManual =
    isBlip &&
    (!blipListUsable ||
      form.templateKey === MANUAL_KEY ||
      (form.templateKey !== '' && listTemplate === undefined));
  // canal da Blip, à mão: o modelo informado
  const blipTemplate =
    blipManual && form.blipTemplateName.trim() !== '' && form.blipTemplateBody.trim() !== ''
      ? {
          name: form.blipTemplateName.trim(),
          language: form.blipTemplateLanguage.trim() || 'pt_BR',
          body: form.blipTemplateBody,
          params: templateParamCount(form.blipTemplateBody),
        }
      : undefined;
  const template = blipManual ? blipTemplate : listTemplate;
  // o modelo já salvo, mesmo fora da lista (manager fora do ar, modo off):
  // DESLIGAR nunca depende do manager — a API aceita o corpo guardado
  const savedKey = rule ? templateKeyOf(rule.templateName, rule.templateLanguage) : null;
  const savedMissing = !isBlip && savedKey !== null && form.templateKey === savedKey && !template;
  const effective =
    template ??
    (savedMissing && !form.enabled && rule
      ? { name: rule.templateName, language: rule.templateLanguage }
      : undefined);
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // canal da Blip: a etapa de resposta acompanha a de envio (o bot acha o ticket nela)
  useEffect(() => {
    if (!isBlip) return;
    setForm((f) =>
      f.repliedStageId === f.sentStageId ? f : { ...f, repliedStageId: f.sentStageId }
    );
  }, [isBlip, form.sentStageId]);

  // o número de parâmetros acompanha o modelo escolhido — a API confere de novo
  const templateParams = template?.params;
  useEffect(() => {
    if (templateParams === undefined) return;
    setForm((f) =>
      f.params.length === templateParams
        ? f
        : {
            ...f,
            params: Array.from(
              { length: templateParams },
              (_, i) => f.params[i] ?? { source: 'first_name' as DispatchParamSource }
            ),
          }
    );
  }, [templateParams]);

  const blocked = form.blockedClientIds
    .split(/[\s,;]+/)
    .filter(Boolean)
    .map(Number);
  const blockedInvalid = blocked.some((n) => !Number.isInteger(n) || n <= 0);
  const preview = template
    ? renderTemplateParams(
        template.body,
        form.params.map((p) =>
          p.source === 'fixed' ? p.value || '…' : `[${dispatchParamSourceLabel(p.source, terms)}]`
        )
      )
    : null;
  const ready =
    form.channelId !== '' &&
    effective !== undefined &&
    (!isBlip || form.repliedStageId === form.sentStageId) &&
    form.entryStageId !== '' &&
    form.sentStageId !== '' &&
    form.invalidStageId !== '' &&
    form.repliedStageId !== '' &&
    form.weekdays.length > 0 &&
    !blockedInvalid;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!ready || !effective) return;
    save.mutate({
      channelId: Number(form.channelId),
      templateName: effective.name,
      templateLanguage: effective.language,
      // canal da Blip: o texto vai junto (o da lista, ou o informado) — a API
      // confere na lista do número e só usa o informado se ela não o tiver
      ...(isBlip && template ? { templateBody: template.body } : {}),
      params: form.params.map((p) => (p.source === 'fixed' ? p : { source: p.source })),
      entryStageId: Number(form.entryStageId),
      sentStageId: Number(form.sentStageId),
      invalidStageId: Number(form.invalidStageId),
      repliedStageId: Number(form.repliedStageId),
      dailyCap: form.dailyCap,
      weekdays: form.weekdays,
      windowStart: form.windowStart,
      windowEnd: form.windowEnd,
      blockedClientIds: blocked,
      enabled: form.enabled,
      requireSheetOnManual: form.requireSheetOnManual,
    });
  };

  const stageSelect = (
    key: 'entryStageId' | 'sentStageId' | 'invalidStageId' | 'repliedStageId',
    label: string,
    hint: string,
    disabled = false
  ) => (
    <div className="space-y-1">
      <Label htmlFor={`dispatch-${key}`}>{label}</Label>
      <Select
        id={`dispatch-${key}`}
        value={String(form[key])}
        disabled={disabled}
        onChange={(e) => set(key, e.target.value === '' ? '' : Number(e.target.value))}
      >
        <option value="">Escolha…</option>
        {stages.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </Select>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-4">
      <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
        <Switch checked={form.enabled} onCheckedChange={(v) => set('enabled', v)} />
        Disparo ligado
      </label>
      {form.enabled && mirroredDesk && !isBlip ? (
        <p className="text-xs text-destructive">
          Mesa espelhada do TiFlux: o disparo só liga pelo número da Blip (e alcança só os tickets
          dos clientes operados no ERP).
        </p>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="dispatch-channel">Canal de WhatsApp</Label>
          <Select
            id="dispatch-channel"
            value={String(form.channelId)}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                channelId: e.target.value === '' ? '' : Number(e.target.value),
                // os modelos são do número: trocar de canal recomeça a escolha
                templateKey: '',
              }))
            }
          >
            <option value="">Escolha…</option>
            {channels.map((c) => (
              // mesa espelhada: só número da Blip (o canal de teste da Meta nunca)
              <option key={c.id} value={c.id} disabled={mirroredDesk && c.provider !== 'blip'}>
                {whatsappChannelLabel(c)}
                {c.active ? '' : ' (inativo)'}
                {mirroredDesk && c.provider !== 'blip' ? ' — só Blip nesta mesa' : ''}
              </option>
            ))}
          </Select>
        </div>
        {isBlip ? (
          <div className="space-y-1">
            <Label htmlFor={blipListUsable ? 'dispatch-blip-list' : 'dispatch-blip-template'}>
              Modelo (conta da Blip)
            </Label>
            {blipListUsable ? (
              <Select
                id="dispatch-blip-list"
                value={blipManual ? MANUAL_KEY : form.templateKey}
                onChange={(e) => set('templateKey', e.target.value)}
              >
                <option value="">Escolha…</option>
                {numberList.templates.map((t) => (
                  <option
                    key={templateKeyOf(t.name, t.language)}
                    value={templateKeyOf(t.name, t.language)}
                  >
                    {t.name} ({t.language})
                  </option>
                ))}
                <option value={MANUAL_KEY}>Outro — informar à mão</option>
              </Select>
            ) : null}
            {blipManual ? (
              <div className="flex gap-2">
                <Input
                  id="dispatch-blip-template"
                  aria-label="Nome do modelo"
                  value={form.blipTemplateName}
                  onChange={(e) => set('blipTemplateName', e.target.value)}
                  placeholder="integraai_agendar_instalacao"
                />
                <Input
                  aria-label="Idioma do modelo"
                  className="w-24"
                  value={form.blipTemplateLanguage}
                  onChange={(e) => set('blipTemplateLanguage', e.target.value)}
                  placeholder="pt_BR"
                />
              </div>
            ) : null}
            {templatesQ.isPending ? (
              <p className="text-xs text-muted-foreground">Carregando os modelos da Blip…</p>
            ) : null}
            {numberList.failures.map((f) => (
              <p key={f.phoneNumberId} className="text-xs text-destructive">
                {templateFailureMessage(f)} Dá para informar o modelo à mão.
              </p>
            ))}
          </div>
        ) : (
          <div className="space-y-1">
            <Label htmlFor="dispatch-template">Modelo</Label>
            <Select
              id="dispatch-template"
              value={form.templateKey}
              onChange={(e) => set('templateKey', e.target.value)}
            >
              <option value="">{templatesQ.isPending ? 'Carregando…' : 'Escolha…'}</option>
              {savedMissing && rule ? (
                <option value={savedKey!}>
                  {rule.templateName} ({rule.templateLanguage}) — salvo
                </option>
              ) : null}
              {numberList.templates.map((t) => (
                <option
                  key={templateKeyOf(t.name, t.language)}
                  value={templateKeyOf(t.name, t.language)}
                >
                  {t.name} ({t.language})
                </option>
              ))}
            </Select>
            {savedMissing && !templatesQ.isPending ? (
              <p className="text-xs text-destructive">
                O modelo salvo não está na lista do canal agora. Dá para salvar a regra desligada;
                para ligar, o modelo precisa voltar à lista.
              </p>
            ) : null}
          </div>
        )}
      </div>

      {blipManual ? (
        <div className="space-y-1">
          <Label htmlFor="dispatch-blip-body">Texto do modelo, como aprovado na Meta</Label>
          <Textarea
            id="dispatch-blip-body"
            value={form.blipTemplateBody}
            maxLength={BLIP_TEMPLATE_BODY_MAX}
            onChange={(e) => set('blipTemplateBody', e.target.value)}
            placeholder="Olá {{1}}! …"
          />
          <p className="text-xs text-muted-foreground">
            Para modelo fora da lista da Blip (ou com a lista sem carregar). O texto vai para o
            histórico da conversa; o que sai é o nome do modelo e os parâmetros abaixo ({'{{1}}'},{' '}
            {'{{2}}'}…).
          </p>
        </div>
      ) : null}

      {template ? (
        <div className="space-y-2">
          {form.params.map((p, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <span className="w-10 text-xs text-muted-foreground">{`{{${i + 1}}}`}</span>
              <Select
                aria-label={`Parâmetro ${i + 1}`}
                className="w-56"
                value={p.source}
                onChange={(e) => {
                  const next = [...form.params];
                  next[i] = { source: e.target.value as DispatchParamSource };
                  set('params', next);
                }}
              >
                {DISPATCH_PARAM_SOURCES.map((src) => (
                  <option key={src} value={src}>
                    {dispatchParamSourceLabel(src, terms)}
                  </option>
                ))}
              </Select>
              {p.source === 'fixed' ? (
                <Input
                  aria-label={`Texto do parâmetro ${i + 1}`}
                  className="w-56"
                  value={p.value ?? ''}
                  onChange={(e) => {
                    const next = [...form.params];
                    next[i] = { source: 'fixed', value: e.target.value };
                    set('params', next);
                  }}
                />
              ) : null}
            </div>
          ))}
          {preview ? (
            <p className="rounded-md border bg-muted/40 p-2 text-xs whitespace-pre-wrap text-muted-foreground">
              {preview}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        {stageSelect(
          'entryStageId',
          'Estágio de entrada',
          'Tickets abertos aqui entram na fila (ex.: Pendente).'
        )}
        {stageSelect(
          'sentStageId',
          'Estágio após o envio',
          isBlip
            ? 'A etapa de agendamento em que o nosso bot procura o ticket (Disparo Realizado).'
            : 'Para onde o ticket vai quando o disparo sai.'
        )}
        {stageSelect(
          'invalidStageId',
          'Estágio de dados inválidos',
          `Sem telefone, sem nome/${terms.resourceId}, ou a Meta recusou.`
        )}
        {stageSelect(
          'repliedStageId',
          'Estágio de resposta',
          isBlip
            ? 'Com o número da Blip, é o mesmo do envio: a resposta vai para o nosso bot, que conduz a conversa.'
            : 'Quando o cliente responde (ex.: Em contato com cliente).',
          isBlip
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1">
          <Label htmlFor="dispatch-cap">Cota por dia</Label>
          <Input
            id="dispatch-cap"
            type="number"
            min={1}
            value={form.dailyCap}
            onChange={(e) => set('dailyCap', Math.max(1, Number(e.target.value) || 1))}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="dispatch-start">Das</Label>
          <Input
            id="dispatch-start"
            type="time"
            value={form.windowStart}
            onChange={(e) => set('windowStart', e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="dispatch-end">Até (exclusive)</Label>
          <Input
            id="dispatch-end"
            type="time"
            value={form.windowEnd}
            onChange={(e) => set('windowEnd', e.target.value)}
          />
        </div>
      </div>

      <div className="space-y-1">
        <span className="text-sm font-medium">Dias (horário de Brasília)</span>
        <div className="flex flex-wrap gap-3">
          {WEEKDAY_LABELS.map((label, day) => (
            <label key={day} className="flex cursor-pointer items-center gap-1.5 text-sm">
              <Checkbox
                checked={form.weekdays.includes(day)}
                onCheckedChange={(v) =>
                  set(
                    'weekdays',
                    v === true
                      ? [...form.weekdays, day].sort((a, b) => a - b)
                      : form.weekdays.filter((d) => d !== day)
                  )
                }
              />
              {label}
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-1">
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
          <Switch
            checked={form.requireSheetOnManual}
            onCheckedChange={(v) => set('requireSheetOnManual', v)}
          />
          Exigir a ficha ao abrir ticket à mão
        </label>
        <p className="text-xs text-muted-foreground">
          Com o disparo ligado, o ticket aberto à mão nesta mesa só é criado com um telefone do{' '}
          {terms.contact} no formato que o disparo aceita (DDD + número) — e com o nome e{' '}
          {terms.resourceId} quando o modelo os usa. Desligar não desliga o disparo: o ticket sem telefone volta a cair em
          “Dados inválidos”.
        </p>
      </div>

      <div className="space-y-1">
        <Label htmlFor="dispatch-blocked">Clientes bloqueados (ids)</Label>
        <Input
          id="dispatch-blocked"
          value={form.blockedClientIds}
          onChange={(e) => set('blockedClientIds', e.target.value)}
          placeholder="395634, 1282218"
          aria-invalid={blockedInvalid}
        />
        <p className="text-xs text-muted-foreground">
          Tickets desses clientes nunca recebem o disparo (ficam registrados como falha).
        </p>
      </div>

      {save.error ? <p className="text-sm text-destructive">{errorMessage(save.error)}</p> : null}
      {save.isSuccess ? <p className="text-sm text-muted-foreground">Regra salva.</p> : null}
      <div className="flex justify-end">
        <Button type="submit" size="sm" disabled={!ready || save.isPending}>
          {save.isPending ? 'Salvando…' : 'Salvar regra'}
        </Button>
      </div>
    </form>
  );
}
